import cN from 'classnames';
import * as React from 'react';
import Link from '../Link';
import styles from './style.m.less';

/**
 *  The Card itself
 */
type CardProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & { isCompact?: boolean };

export function Card({ className, isCompact = false, ...props }: CardProps) {
    return <article className={cN('card', className, { [styles.compact]: isCompact })} {...props} />;
}

interface LinkCardProps extends React.ComponentPropsWithoutRef<typeof Link> {
    isCompact?: boolean;
}

/**
 * An interactive card with a link (via href prop).
 */
export function LinkCard({ className, isCompact = false, ...props }: LinkCardProps) {
    return (
        <Link
            className={cN('card', styles.link, className, {
                [styles.compact]: isCompact,
            })}
            {...props}
        />
    );
}

/**
 *  The Card Header inside a card
 *  A CardHeader has CardHeaderHeading and CardHeaderButtons inside it
 */
export function CardHeader({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>) {
    return <header className={cN('card-header', className)} {...props} />;
}

export function CardHeaderHeading({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) {
    return <h1 className={cN('card-header-heading', className)} {...props} />;
}

export function CardHeaderButtons({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
    return <div className={cN('card-header-buttons', className)} {...props} />;
}

/**
 *  A Section inside a card
 *  A CardSection has CardSectionHeading and CardBody inside it
 */
export function CardSection({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>) {
    return <section className={cN('card-section', className)} {...props} />;
}

export function CardSectionHeading({
    className,
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLHeadingElement>, HTMLHeadingElement>) {
    return <h1 className={cN('card-section-heading', className)} {...props} />;
}

export function CardSectionBody({
    ...props
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>) {
    return <div {...props} />;
}

/**
 *  A card thumbnail, that represents a (often clickable) “preview” of a card,
 *  e.g. on an overview page
 */
type ThumbCardProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
    isCompact?: boolean;
    isEnabled?: boolean;
};

export function ThumbCard({ className, isEnabled = true, isCompact = false, ...props }: ThumbCardProps) {
    return (
        <article
            className={cN(
                'card',
                styles.interactive,
                isEnabled ? styles.interactiveEnabled : styles.interactiveDisabled,
                { [styles.compact]: isCompact },
                className
            )}
            {...props}
        />
    );
}
